/**
 * Utility functions for working with images
 */

/**
 * Checks if a URL points to an image resource
 * @param url The URL to check
 * @param mediaType Optional media type string to help determine if it's an image
 * @returns boolean indicating if the URL is likely an image
 */
export const isImageUrl = (url: string, mediaType?: string): boolean => {
  // If mediaType is provided, use it as the primary indicator
  if (mediaType) {
    // Check if MediaType indicates it's an image
    const isImageType = mediaType && mediaType.toLowerCase().includes("image");
    if (isImageType) {
      return true;
    }

    // If mediaType is provided but doesn't indicate an image, check URL as fallback
  }

  // Check for common image extensions
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".svg",
    ".bmp",
    ".tiff",
  ];
  const lowercaseUrl = url.toLowerCase();

  // Check if URL ends with an image extension
  const hasImageExtension = imageExtensions.some((ext) =>
    lowercaseUrl.endsWith(ext)
  );

  // Check if URL contains image format indicators
  const containsImageIndicator =
    lowercaseUrl.includes("/image/") ||
    lowercaseUrl.includes("format=jpg") ||
    lowercaseUrl.includes("format=png") ||
    lowercaseUrl.includes("content-type=image");

  // Explicitly filter out video URLs
  const isVideoUrl =
    lowercaseUrl.includes("videodelivery.net") ||
    lowercaseUrl.includes("/video/") ||
    lowercaseUrl.includes("iframe");

  return (hasImageExtension || containsImageIndicator) && !isVideoUrl;
};

/**
 * Process media items and return an array of image URLs
 * @param media Array of media items
 * @param options Configuration options
 * @returns Array of image URLs
 */
export const getImageUrlsFromMedia = (
  media: any[],
  options: {
    mustRemovePhotos?: boolean;
    mustRemovePrimaryPhoto?: boolean;
    maxImages?: number;
    debugLog?: boolean;
  } = {}
): string[] => {
  const {
    mustRemovePhotos = false,
    mustRemovePrimaryPhoto = false,
    maxImages = 1,
    debugLog = false,
  } = options;

  if (!Array.isArray(media) || media.length === 0) {
    return [];
  }

  // If primary photo must be removed, use placeholder or empty array
  if (mustRemovePrimaryPhoto) {
    if (debugLog) console.log("Must remove primary photo");
    return [];
  }

  // Filter media to only include images based on MediaType
  const imageMedia = media.filter(
    (item) => item.MediaURL && isImageUrl(item.MediaURL, item.MediaType)
  );

  if (debugLog) {
    console.log("Media filtering:", {
      totalMedia: media.length,
      imageMediaCount: imageMedia.length,
      firstFewTypes: media.slice(0, 3).map((m) => m.MediaType),
    });
  }

  // If no images found, return empty array
  if (imageMedia.length === 0) {
    if (debugLog) console.log("No image media found");
    return [];
  }

  // If must remove photos, only use primary photo if it exists
  if (mustRemovePhotos) {
    const primaryImage = imageMedia.find((item) => item.Order === 0);
    if (primaryImage?.MediaURL) {
      if (debugLog) console.log("Using primary photo (mustRemovePhotos=true)");
      return [primaryImage.MediaURL];
    }
    return [];
  }

  // Try to find primary image first (Order = 0)
  const primaryImage = imageMedia.find((item) => item.Order === 0);
  if (primaryImage?.MediaURL) {
    if (debugLog) console.log("Using primary image URL");
    return [primaryImage.MediaURL];
  }

  // Otherwise use the first few images in the filtered array
  const urls = imageMedia
    .slice(0, maxImages)
    .map((item) => item.MediaURL)
    .filter(Boolean);

  if (urls.length > 0) {
    if (debugLog)
      console.log(`Using first ${urls.length} available image URLs`);
    return urls;
  }

  if (debugLog) console.log("No valid image URLs found");
  return [];
};

/**
 * Get optimized image URL with width and format parameters
 * @param url The image URL to optimize
 * @param width The desired width for the image
 * @returns Optimized image URL with width and webp format parameters
 */
export const getOptimizedImageUrl = (url: string, width: number): string => {
  if (!url) return url;

  // Add width and format parameters to the URL
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}d=${width}&format=webp`;
};
